import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { finalize } from "rxjs";
import { DataService } from "../../../services/api/data/data.service";
import { LoadingService } from "../../../services/loading/loading";
import { DropdownComponent } from "../../utils/dropdown/dropdown.component";

interface FieldConfigRow {
  id: number;
  createdAt: string;
  updatedAt: string;
  emailList: string[];
}

interface FieldConfigErrors {
  fieldId?: string;
  emailList?: string;
}

interface FieldConfigFilterOption {
  key: string;
  label: string;
}

interface FieldConfigSelectOption {
  key: number;
  label: string;
}

@Component({
  selector: "app-field-config-dashboard",
  standalone: true,
  imports: [CommonModule, FormsModule, DropdownComponent],
  host: {
    class: "block h-full w-full min-w-0",
  },
  templateUrl: "./field-config-dashboard.html",
  styleUrl: "./field-config-dashboard.scss",
})
export class FieldConfigDashboard implements OnInit {
  rows: FieldConfigRow[] = [];
  availableFieldIds: number[] = [];
  filteredRows: FieldConfigRow[] = [];
  paginatedRows: FieldConfigRow[] = [];
  searchTerm = "";
  selectedFieldIdFilter: string | null = null;
  selectedEmailFilter: string | null = null;
  sortBy: "Field ID" | "Updated" = "Updated";
  sortDirection: "asc" | "desc" = "desc";
  hasUserSorted = false;
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;
  totalPages = 0;
  itemsPerPageOptions = [5, 10, 25, 50];
  Math = Math;
  isAddModalOpen = false;
  isEditModalOpen = false;
  isDeleteModalOpen = false;
  selectedRow: FieldConfigRow | null = null;
  newRow: FieldConfigRow = this.createEmptyRow();
  editRow: FieldConfigRow = this.createEmptyRow();
  newRowErrors: FieldConfigErrors = {};
  editRowErrors: FieldConfigErrors = {};
  newRowEmailInput = "";
  editRowEmailInput = "";
  fieldIdFilterOptions: FieldConfigFilterOption[] = [];
  emailFilterOptions: FieldConfigFilterOption[] = [];
  addFieldIdOptions: FieldConfigSelectOption[] = [];
  editFieldIdOptions: FieldConfigSelectOption[] = [];

  constructor(
    private dataService: DataService,
    private loadingService: LoadingService,
  ) {}

  ngOnInit(): void {
    this.fetchFieldConfigs();
    this.fetchAvailableFieldIds();
  }

  fetchFieldConfigs(): void {
    this.loadingService.start({
      token: "field-config-dashboard",
      message: "Loading fields",
    });
    this.dataService
      .getFieldsConfig()
      .pipe(finalize(() => this.loadingService.stop("field-config-dashboard")))
      .subscribe({
        next: (response) => {
          const rows = Array.isArray(response) ? response : [];
          this.rows = rows.map((item: any) => {
            const rawId = item?.uniqueid;
            const parsedId = Number(rawId);
            const emails = item?.allowed_emails ?? [];
            return {
              id: Number.isFinite(parsedId) ? parsedId : 0,
              createdAt: item?.createdat ?? "",
              updatedAt: item?.updatedat ?? "",
              emailList:
                Array.isArray(emails) ?
                  emails
                    .filter((email: any) => typeof email === "string")
                    .map((email: string) => email.trim().toLowerCase())
                : [],
            };
          });
          this.refreshModalFieldIdOptions();
          this.refreshFilterOptions();
          this.applyFilters();
        },
        error: (error) => {
          console.error("Error fetching field config rows:", error);
          this.rows = [];
          this.refreshModalFieldIdOptions();
          this.refreshFilterOptions();
          this.applyFilters();
        },
      });
  }

  createEmptyRow(): FieldConfigRow {
    return {
      id: 0,
      createdAt: "",
      updatedAt: "",
      emailList: [],
    };
  }

  private buildAvailableAddFieldIdOptions(): FieldConfigSelectOption[] {
    return this.availableFieldIds.map((id) => ({ key: id, label: String(id) }));
  }

  private buildAvailableEditFieldIdOptions(): FieldConfigSelectOption[] {
    if (!this.editRow.id) {
      return [];
    }
    return [{ key: this.editRow.id, label: String(this.editRow.id) }];
  }

  onSearchChange(): void {
    this.currentPage = 1;
    this.applyFilters();
  }

  onSelectedColumnFilterChange(
    column: "fieldId" | "emailList",
    value: string | null,
  ): void {
    if (column === "fieldId") {
      this.selectedFieldIdFilter = value;
    } else {
      this.selectedEmailFilter = value;
    }
    this.currentPage = 1;
    this.applyFilters();
  }

  clearColumnFilters(): void {
    this.selectedFieldIdFilter = null;
    this.selectedEmailFilter = null;
    this.currentPage = 1;
    this.applyFilters();
  }

  hasColumnFilters(): boolean {
    return !!this.selectedFieldIdFilter || !!this.selectedEmailFilter;
  }

  applyFilters(): void {
    const query = this.searchTerm.trim().toLowerCase();
    this.filteredRows = this.rows.filter((row) => {
      const matchesSearch =
        !query ||
        String(row.id).includes(query) ||
        row.emailList.some((email) => email.toLowerCase().includes(query));
      const matchesFieldId =
        !this.selectedFieldIdFilter ||
        String(row.id) === this.selectedFieldIdFilter;
      const matchesEmail =
        !this.selectedEmailFilter ||
        row.emailList.includes(this.selectedEmailFilter);

      return matchesSearch && matchesFieldId && matchesEmail;
    });
    if (this.hasUserSorted) {
      this.sortRows();
    } else {
      this.updatePagination();
    }
  }

  getFilterDisplayLabel(column: "fieldId" | "emailList"): string {
    return column === "fieldId" ? "Field ID" : "Email List";
  }

  private refreshFilterOptions(): void {
    const uniqueFieldIds = Array.from(
      new Set(this.rows.map((row) => String(row.id))),
    ).sort((a, b) => Number(a) - Number(b));
    const uniqueEmails = Array.from(
      new Set(
        this.rows.flatMap((row) =>
          row.emailList.map((email) => email.toLowerCase()),
        ),
      ),
    ).sort((a, b) => a.localeCompare(b));

    this.fieldIdFilterOptions = uniqueFieldIds.map((id) => ({
      key: id,
      label: id,
    }));
    this.emailFilterOptions = uniqueEmails.map((email) => ({
      key: email,
      label: email,
    }));

    if (
      this.selectedFieldIdFilter &&
      !uniqueFieldIds.includes(this.selectedFieldIdFilter)
    ) {
      this.selectedFieldIdFilter = null;
    }

    if (
      this.selectedEmailFilter &&
      !uniqueEmails.includes(this.selectedEmailFilter)
    ) {
      this.selectedEmailFilter = null;
    }
  }

  sortRows(): void {
    const dir = this.sortDirection === "asc" ? 1 : -1;
    this.filteredRows.sort((a, b) => {
      switch (this.sortBy) {
        case "Field ID":
          return (a.id - b.id) * dir;
        case "Updated":
        default:
          return (
            (new Date(a.updatedAt).getTime() -
              new Date(b.updatedAt).getTime()) *
              dir || a.id - b.id
          );
      }
    });
    this.updatePagination();
  }

  onHeaderSort(sort: "Field ID" | "Updated"): void {
    this.hasUserSorted = true;
    if (this.sortBy === sort) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortBy = sort;
      this.sortDirection = sort === "Updated" ? "desc" : "asc";
    }
    this.sortRows();
  }

  getSortIcon(key: "Field ID" | "Updated"): "up" | "down" | "neutral" {
    if (!this.hasUserSorted) return "neutral";
    if (this.sortBy !== key) return "neutral";
    return this.sortDirection === "asc" ? "up" : "down";
  }

  updatePagination(): void {
    this.totalItems = this.filteredRows.length;
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage) || 1;
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedRows = this.filteredRows.slice(startIndex, endIndex);
  }

  changeItemsPerPage(): void {
    this.currentPage = 1;
    this.updatePagination();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  getVisiblePages(): number[] {
    const delta = 2;
    const range: number[] = [];
    const rangeWithDots: number[] = [];
    for (
      let i = Math.max(2, this.currentPage - delta);
      i <= Math.min(this.totalPages - 1, this.currentPage + delta);
      i++
    ) {
      range.push(i);
    }
    if (this.currentPage - delta > 2) {
      rangeWithDots.push(1, -1);
    } else {
      rangeWithDots.push(1);
    }
    rangeWithDots.push(...range);
    if (this.totalPages > 1) {
      if (this.currentPage + delta < this.totalPages - 1) {
        rangeWithDots.push(-1, this.totalPages);
      } else {
        rangeWithDots.push(this.totalPages);
      }
    }
    return rangeWithDots;
  }

  formatDateTime(dateString?: string): string {
    if (!dateString) return "N/A";
    const trimmed = dateString.trim();
    const match = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})/,
    );
    if (!match) {
      return trimmed;
    }
    const [, year, month, day, hourStr, minute, second] = match;
    let hour = Number(hourStr);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    if (hour === 0) hour = 12;
    const hourPadded = String(hour).padStart(2, "0");
    return `${month}/${day}/${year}, ${hourPadded}:${minute}:${second} ${ampm}`;
  }

  openAddModal(): void {
    this.newRow = this.createEmptyRow();
    this.newRowErrors = {};
    this.newRowEmailInput = "";
    this.resetModalFieldIdOptions();
    this.isAddModalOpen = true;
  }

  openEditModal(row: FieldConfigRow): void {
    this.editRow = { ...row, emailList: [...row.emailList] };
    this.editRowErrors = {};
    this.editRowEmailInput = "";
    this.resetModalFieldIdOptions();
    this.isEditModalOpen = true;
  }

  onModalFieldIdChange(
    target: "new" | "edit",
    value: string | number | null,
  ): void {
    const parsedValue = Number(value);
    const nextValue =
      Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
    if (target === "new") {
      this.newRow.id = nextValue;
      this.newRowErrors = { ...this.newRowErrors, fieldId: undefined };
      return;
    }

    this.editRow.id = nextValue;
    this.editRowErrors = { ...this.editRowErrors, fieldId: undefined };
  }

  openDeleteModal(row: FieldConfigRow): void {
    this.selectedRow = row;
    this.isDeleteModalOpen = true;
  }

  closeModals(): void {
    this.isAddModalOpen = false;
    this.isEditModalOpen = false;
    this.isDeleteModalOpen = false;
    this.selectedRow = null;
    this.newRowEmailInput = "";
    this.editRowEmailInput = "";
  }

  validateRow(row: FieldConfigRow, isEdit: boolean): boolean {
    const errors: FieldConfigErrors = {};
    if (!Number.isInteger(row.id) || row.id <= 0) {
      errors.fieldId = "Field ID is required and must be a positive integer";
    } else if (
      !isEdit &&
      this.rows.some((existing) => existing.id === row.id)
    ) {
      errors.fieldId = "Field ID already exists";
    }
    if (row.emailList.length === 0) {
      errors.emailList = "Email List is required";
    } else if (row.emailList.some((email) => !this.isValidEmail(email))) {
      errors.emailList = "One or more emails are invalid";
    }
    if (isEdit) {
      this.editRowErrors = errors;
    } else {
      this.newRowErrors = errors;
    }

    return Object.keys(errors).length === 0;
  }

  addEmailToRow(target: "new" | "edit"): void {
    const inputValue =
      target === "new" ?
        this.newRowEmailInput.trim()
      : this.editRowEmailInput.trim();
    if (!inputValue) return;
    const email = inputValue.toLowerCase();
    if (!this.isValidEmail(email)) {
      if (target === "new") {
        this.newRowErrors = {
          ...this.newRowErrors,
          emailList: "Please enter a valid email",
        };
      } else {
        this.editRowErrors = {
          ...this.editRowErrors,
          emailList: "Please enter a valid email",
        };
      }
      return;
    }
    const list =
      target === "new" ? this.newRow.emailList : this.editRow.emailList;
    if (!list.includes(email)) {
      list.push(email);
    }
    if (target === "new") {
      this.newRowEmailInput = "";
      this.newRowErrors = { ...this.newRowErrors, emailList: undefined };
    } else {
      this.editRowEmailInput = "";
      this.editRowErrors = { ...this.editRowErrors, emailList: undefined };
    }
  }

  removeEmailFromRow(target: "new" | "edit", email: string): void {
    if (target === "new") {
      this.newRow.emailList = this.newRow.emailList.filter(
        (item) => item !== email,
      );
      this.newRowErrors = {
        ...this.newRowErrors,
        emailList:
          this.newRow.emailList.length === 0 ?
            "Email List is required"
          : undefined,
      };
    } else {
      this.editRow.emailList = this.editRow.emailList.filter(
        (item) => item !== email,
      );
      this.editRowErrors = {
        ...this.editRowErrors,
        emailList:
          this.editRow.emailList.length === 0 ?
            "Email List is required"
          : undefined,
      };
    }
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  addRow(): void {
    if (!this.validateRow(this.newRow, false)) {
      return;
    }
    const payload = {
      uniqueid: this.newRow.id,
      allowed_emails: this.newRow.emailList,
    };
    this.runFieldConfigAction({
      token: "field-config-create",
      message: "Creating field config",
      action: () => this.dataService.createFieldsConfig(payload),
      errorMessage: "Error creating field config row:",
      onErrorMessage: (message) => {
        if (message) {
          this.newRowErrors = { ...this.newRowErrors, fieldId: message };
        }
      },
    });
  }

  updateRow(): void {
    if (!this.validateRow(this.editRow, true)) {
      return;
    }
    const payload = {
      allowed_emails: this.editRow.emailList,
    };
    this.runFieldConfigAction({
      token: "field-config-update",
      message: "Updating field config",
      action: () =>
        this.dataService.updateFieldsConfig(this.editRow.id, payload),
      errorMessage: "Error updating field config row:",
      onErrorMessage: (message) => {
        if (message) {
          this.editRowErrors = { ...this.editRowErrors, fieldId: message };
        }
      },
    });
  }

  confirmDelete(): void {
    if (!this.selectedRow) return;
    const selectedId = this.selectedRow.id;
    this.runFieldConfigAction({
      token: "field-config-delete",
      message: "Deleting field config",
      action: () => this.dataService.deleteFieldsConfig(selectedId),
      errorMessage: "Error deleting field config row:",
    });
  }

  trackByRowId(index: number, row: FieldConfigRow): number {
    return row.id;
  }

  private refreshModalFieldIdOptions(): void {
    this.addFieldIdOptions = this.buildAvailableAddFieldIdOptions();
    this.editFieldIdOptions = this.buildAvailableEditFieldIdOptions();
  }

  private resetModalFieldIdOptions(): void {
    this.refreshModalFieldIdOptions();
  }

  private fetchAvailableFieldIds(): void {
    this.dataService.getUnselectedFieldIds().subscribe({
      next: (response) => {
        this.availableFieldIds = this.normalizeFieldIdResponse(response);
        this.refreshModalFieldIdOptions();
      },
      error: (error) => {
        console.error("Error fetching available field IDs:", error);
        this.availableFieldIds = [];
        this.refreshModalFieldIdOptions();
      },
    });
  }

  private normalizeFieldIdResponse(response: any): number[] {
    const rawItems: unknown[] =
      Array.isArray(response) ? response
      : Array.isArray(response?.items) ? response.items
      : [];

    const normalizedIds = rawItems
      .map((item: unknown) => {
        if (typeof item === "number" || typeof item === "string") {
          return Number(item);
        }

        if (item && typeof item === "object") {
          const source = item as {
            id?: number | string;
            fieldId?: number | string;
            uniqueid?: number | string;
            uniqueId?: number | string;
          };
          return Number(
            source.id ?? source.fieldId ?? source.uniqueid ?? source.uniqueId,
          );
        }

        return Number.NaN;
      })
      .filter((id: number) => Number.isFinite(id) && id > 0);

    return Array.from(new Set<number>(normalizedIds)).sort(
      (a: number, b: number) => a - b,
    );
  }

  private runFieldConfigAction(options: {
    token: string;
    message: string;
    action: () => import("rxjs").Observable<any>;
    errorMessage: string;
    onErrorMessage?: (message: string) => void;
  }): void {
    this.loadingService.start({
      token: options.token,
      message: options.message,
    });
    options
      .action()
      .pipe(finalize(() => this.loadingService.stop(options.token)))
      .subscribe({
        next: () => {
          this.closeModals();
          this.fetchFieldConfigs();
          this.fetchAvailableFieldIds();
        },
        error: (error) => {
          console.error(options.errorMessage, error);
          const message = error?.error?.message || error?.message || "";
          if (options.onErrorMessage) {
            options.onErrorMessage(message);
          }
        },
      });
  }
}
